import { create } from 'zustand';
import { apiRequest } from '../lib/api';
import { ApiError } from '../lib/http';
import { Collection, CollectionPage } from '../types/api';
import { PAGE_SIZE } from '../lib/constants';

type PageState = {
  data?: CollectionPage;
  loading: boolean;
  error: string;
  unavailable?: boolean;
};
interface State {
  home: Collection[];
  homeLoaded: boolean;
  homeLoading: boolean;
  homeError: string;
  pages: Record<string, PageState>;
  fetchHome: () => Promise<void>;
  fetchPage: (slug: string, more?: boolean) => Promise<void>;
  /** Mark the home collections stale so the next fetchHome reloads them. */
  invalidateHome: () => void;
  /** Drop one collection page's cached products and fetch it again. */
  reloadPage: (slug: string) => Promise<void>;
}
const normalize = (data: Collection): Collection => ({
  ...data,
  items: data.items.map((p) => ({ ...p, price: Number(p.price) })),
});
export const useCollectionStore = create<State>((set, get) => ({
  home: [],
  homeLoaded: false,
  homeLoading: false,
  homeError: '',
  pages: {},
  fetchHome: async () => {
    if (get().homeLoaded || get().homeLoading) return;
    set({ homeLoading: true, homeError: '' });
    try {
      const data = await apiRequest<Collection[]>('/home/collections');
      set({ home: data.map(normalize), homeLoaded: true, homeLoading: false });
    } catch {
      set({ homeLoading: false, homeError: 'Could not load collections. Please try again.' });
    }
  },
  fetchPage: async (slug, more = false) => {
    const previous = get().pages[slug];
    if (previous?.loading || (previous?.data && !more)) return;
    if (more && previous?.data && !previous.data.has_more) return;
    const offset = more ? (previous?.data?.items.length ?? 0) : 0;
    const update = (patch: Partial<PageState>) =>
      set((s) => ({
        pages: {
          ...s.pages,
          [slug]: { ...(s.pages[slug] ?? { loading: false, error: '' }), ...patch },
        },
      }));
    update({ loading: true, error: '', unavailable: false });
    try {
      const data = await apiRequest<CollectionPage>(
        `/collections/${encodeURIComponent(slug)}/items?offset=${offset}&limit=${PAGE_SIZE}`,
      );
      const normalized = { ...data, ...normalize(data) };
      const items = more ? [...(previous?.data?.items ?? []), ...normalized.items] : normalized.items;
      update({
        loading: false,
        data: { ...normalized, items: Array.from(new Map(items.map((p) => [p.item_id, p])).values()) },
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) update({ loading: false, unavailable: true });
      else update({ loading: false, error: 'Could not load products. Please try again.' });
    }
  },
  invalidateHome: () => set({ homeLoaded: false }),
  reloadPage: (slug) => {
    set((s) => ({ pages: { ...s.pages, [slug]: { loading: false, error: '' } } }));
    return get().fetchPage(slug);
  },
}));
